import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AdminContext = createContext(null);
const STORAGE_KEY = "bs_admin_token";

export const AdminProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

    useEffect(() => {
        if (token) localStorage.setItem(STORAGE_KEY, token);
        else localStorage.removeItem(STORAGE_KEY);
    }, [token]);

    const login = useCallback(async (password) => {
        const r = await api.post("/admin/login", { password });
        const t = r.data?.token;
        if (!t) throw new Error("No token");
        setToken(t);
        return true;
    }, []);

    const logout = useCallback(() => setToken(""), []);

    const adminGet = useCallback(
        async (path) => {
            const r = await api.get(path, { headers: { "X-Admin-Password": token } });
            return r.data;
        },
        [token],
    );

    const adminPatch = useCallback(
        async (path, data) => {
            const r = await api.patch(path, data, {
                headers: { "X-Admin-Password": token },
            });
            return r.data;
        },
        [token],
    );

    const adminPost = useCallback(
        async (path, data) => {
            const r = await api.post(path, data || {}, {
                headers: { "X-Admin-Password": token },
            });
            return r.data;
        },
        [token],
    );

    return (
        <AdminContext.Provider
            value={useMemo(
                () => ({ token, isAuthed: !!token, login, logout, adminGet, adminPatch, adminPost }),
                [token, login, logout, adminGet, adminPatch, adminPost],
            )}
        >
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => {
    const ctx = useContext(AdminContext);
    if (!ctx) throw new Error("useAdmin must be used within AdminProvider");
    return ctx;
};
